import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Avatar from '@mui/material/Avatar';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { NAV } from './configLayout';
import { usePathname } from '../../../hooks/usePathname';
import { useResponsive } from '../../../hooks/useResponsive';
import { account } from '../../../_mock/account';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import SvgColor from '../../../components/svgColor';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import NavSectionVertical from './navbar/navSectionVertical';
import { NavConfig } from './navbar/navItem';
import Label from '../../../components/label';

interface NavProps {
  openNav: boolean;
  onCloseNav: () => void;
}

const icon = (name: string) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
);

export default function Nav({ openNav, onCloseNav }: NavProps) {
  const pathname = usePathname();
  const { t } = useTranslation();
  const { user } = useAuth();

  const navConfig = [
    {
      subheader: 'general',
      items: [
        {
          title: t('dashboard'),
          path: '/',
          icon: icon('ic_analytics'),
        },
        {
          title: t('pointsOfSale'),
          path: '/points',
          icon: icon('ic_ecommerce'),
        },
        {
          title: t('payments'),
          path: '/payments',
          icon: icon('ic_banking'),
        },
        {
          title: t('invoices'),
          path: '/invoices',
          icon: icon('ic_invoice'),
          children: [
            {
              title: t('list'),
              path: '/invoices',
              icon: icon('ic_ecommerce'),
            },
            {
              title: t('newInvoice'),
              path: '/invoices/create',
              icon: icon('ic_ecommerce'),
              info: (
                <Label color="primary">
                  new
                </Label>
              ),
            },
          ],
        },
        {
          title: t('refunds'),
          path: '/refunds',
          icon: icon('ic_user'),
        },
      ],
    },
  ] as NavConfig;

  const upLg = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderAccount = (
    <Box
      sx={{
        my: 3,
        mx: 2.5,
        py: 2,
        px: 2.5,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
      }}
    >
      <Avatar src={account.photoURL} alt="photoURL" />

      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2">
          {t('partnerId')} {user?.partnerId}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('partner')}
        </Typography>
      </Box>
    </Box>
  );

  const renderMenu = <NavSectionVertical navConfig={navConfig} />;

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo disabledLink={false} sx={{ mt: 3, ml: 4 }} />
      {renderAccount}
      {renderMenu}
    </Scrollbar>
  );

  return (
    <Box
      component={'aside'}
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {upLg ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.WIDTH,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
